import { useUrlParams } from '../../hooks/useUrlParams';
import React, { useState, useEffect, useRef } from 'react';
import { useApi } from '../../hooks/useApi';
import MilestoneLineChart from '../../components/MilestoneLineChart';
import { METRIC_COLORS } from './TaskResultLinksSection';
import { OZON_COLS, WB_COLS } from '../marketplace/shared/DailyStatsLogs';

function dateStr(d) { return d.toISOString().split('T')[0]; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

const RATE_KEYS = new Set([...OZON_COLS, ...WB_COLS].filter(c => c.isRate).map(c => c.key));
// Те же показатели, что в «Статистика → Логи»; выручка и заказы уже есть общими кнопками.
const COMMON_METRICS = [
    { key: 'orders_revenue', label: 'Выручка, ₽', color: 'revenue' },
    { key: 'orders_units', label: 'Заказы, ед.', color: 'orders' },
];
const SKIP_KEYS = new Set(['revenue', 'ordered_units', 'order_sum', 'order_count']);

// Sum daily rows from multiple cabinets into one series (conversion rates are averaged)
function sumByDate(arrays) {
    const map = {};
    const counts = {};
    arrays.flat().forEach(item => {
        const d = item.date;
        if (!map[d]) { map[d] = { date: d }; counts[d] = {}; }
        Object.entries(item).forEach(([k, v]) => {
            if (k === 'date' || v === null || v === undefined || isNaN(Number(v))) return;
            map[d][k] = (map[d][k] || 0) + Number(v);
            counts[d][k] = (counts[d][k] || 0) + 1;
        });
    });
    Object.values(map).forEach(row => {
        Object.keys(row).forEach(k => {
            if (RATE_KEYS.has(k)) row[k] = row[k] / counts[row.date][k];
        });
        row.orders_units = row.orders_units || 0;
        row.orders_revenue = row.orders_revenue || 0;
    });
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

export default function TasksDynamicsTab({
    workspaceId: initialWorkspaceId,
    companyId,
    marketplaces = [],
    visibleWorkspaces = [],
}) {
    const { request, showStatus } = useApi();
    const { update: updateUrlParams } = useUrlParams();

    // Workspace selection
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(initialWorkspaceId || '');

    // Derive workspace and its cabinets (Ozon + WB)
    const workspace = visibleWorkspaces.find(w => String(w.id) === String(selectedWorkspaceId));
    const workspaceCabinetIds = (workspace?.cabinets || []).map(c => String(c.marketplace_account_id));
    const workspaceCabinets = marketplaces.filter(
        m => workspaceCabinetIds.includes(String(m.id))
    );

    // Cabinet selection: all or single
    const [allCabinets, setAllCabinets] = useState(true);
    const [cabinetId, setCabinetId] = useState('');

    // Period
    const [periodPreset, setPeriodPreset] = useState('30'); // '30' | 'custom'
    const [dateFrom, setDateFrom] = useState(() => dateStr(addDays(new Date(), -30)));
    const [dateTo, setDateTo] = useState(() => dateStr(addDays(new Date(), -1)));

    // Metric display
    const [metricType, setMetricType] = useState('orders_revenue');

    // Per-SKU charts state
    const [extraSkus, setExtraSkus] = useState([]);
    const [skuSearch, setSkuSearch] = useState('');
    const [skuResults, setSkuResults] = useState([]);
    const [searchBusy, setSearchBusy] = useState(false);
    const skuSearchRef = useRef(null);

    // Data
    const [assortmentData, setAssortmentData] = useState([]);
    const [skuData, setSkuData] = useState({});
    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [dataRange, setDataRange] = useState(null); // {min, max} available data range

    // Pre-select workspace from prop
    useEffect(() => {
        if (initialWorkspaceId && initialWorkspaceId !== selectedWorkspaceId) {
            setSelectedWorkspaceId(initialWorkspaceId); // текущий проект сменился в адресе
        }
    }, [initialWorkspaceId]); // eslint-disable-line

    // Reset when workspace changes
    useEffect(() => {
        setAllCabinets(true);
        setCabinetId('');
        setExtraSkus([]);
        setSkuData({});
        setAssortmentData([]);
        setMilestones([]);
        setHasLoaded(false);
        setDataRange(null);
    }, [selectedWorkspaceId]);

    // Reset per-SKU charts when cabinet selection changes
    useEffect(() => {
        setExtraSkus([]);
        setSkuData({});
        setSkuSearch('');
        setSkuResults([]);
    }, [cabinetId, allCabinets]);

    // Close SKU dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (skuSearchRef.current && !skuSearchRef.current.contains(e.target)) {
                setSkuResults([]);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Debounced SKU search — only for single specific cabinet
    useEffect(() => {
        if (allCabinets || !cabinetId || !skuSearch.trim()) {
            setSkuResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchBusy(true);
            try {
                const results = await request(
                    `/api/v1/marketplaces/${cabinetId}/products?q=${encodeURIComponent(skuSearch)}&limit=20`
                );
                setSkuResults(results || []);
            } catch { setSkuResults([]); }
            finally { setSearchBusy(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [skuSearch, cabinetId, allCabinets]);

    const addSkuChart = (product) => {
        const key = `${cabinetId}:${product.sku}`;
        if (!extraSkus.find(e => e.key === key)) {
            setExtraSkus(prev => [...prev, {
                key,
                sku: product.sku,
                name: product.name,
                cabinetId,
            }]);
        }
        setSkuSearch('');
        setSkuResults([]);
    };

    const removeSkuChart = (key) => {
        setExtraSkus(prev => prev.filter(e => e.key !== key));
        setSkuData(prev => { const n = { ...prev }; delete n[key]; return n; });
    };

    const getDateRange = (overrideFrom, overrideTo) => {
        if (overrideFrom && overrideTo) return { from: overrideFrom, to: overrideTo };
        if (periodPreset === '30') {
            return {
                from: dateStr(addDays(new Date(), -30)),
                to:   dateStr(addDays(new Date(), -1)),
            };
        }
        return { from: dateFrom, to: dateTo };
    };

    const loadData = async (overrideFrom, overrideTo) => {
        if (!selectedWorkspaceId) { showStatus('error', 'Выберите проект'); return; }

        const cabinetsToLoad = allCabinets
            ? workspaceCabinets
            : workspaceCabinets.filter(c => String(c.id) === String(cabinetId));

        if (cabinetsToLoad.length === 0) {
            showStatus('error', 'Нет кабинетов в выбранном проекте');
            return;
        }

        const { from, to } = getDateRange(overrideFrom, overrideTo);
        if (!from || !to) { showStatus('error', 'Укажите даты начала и конца периода'); return; }
        if (from > to) { showStatus('error', 'Дата начала периода позже даты конца — поменяйте их местами'); return; }
        setLoading(true);
        try {
            // Assortment data: parallel per cabinet, then sum
            const rawArrays = await Promise.all(
                cabinetsToLoad.map(cab =>
                    request(
                        `/api/v1/tasks/daily-metrics?account_id=${cab.id}&date_from=${from}&date_to=${to}`
                    ).catch(() => [])
                )
            );
            const merged = sumByDate(rawArrays);
            setAssortmentData(merged);

            // If no data returned, probe ALL cabinets across wide range to find available data
            if (merged.length === 0) {
                const probeArrays = await Promise.all(
                    cabinetsToLoad.map(cab =>
                        request(
                            `/api/v1/tasks/daily-metrics?account_id=${cab.id}&date_from=2020-01-01&date_to=2030-12-31`
                        ).catch(() => [])
                    )
                );
                const probeAll = sumByDate(probeArrays);
                if (probeAll.length > 0) {
                    setDataRange({ min: probeAll[0].date, max: probeAll[probeAll.length - 1].date });
                } else {
                    setDataRange(null);
                }
            } else {
                setDataRange(null);
            }

            // Milestones by workspace (optionally filtered by specific cabinet)
            const milestoneUrl =
                `/api/v1/tasks/milestones?workspace_id=${selectedWorkspaceId}` +
                (!allCabinets && cabinetId ? `&marketplace_account_id=${cabinetId}` : '');
            const milestoneData = await request(milestoneUrl).catch(() => []);
            setMilestones(milestoneData || []);

            // Per-SKU charts
            if (extraSkus.length > 0) {
                const skuFetches = await Promise.all(
                    extraSkus.map(({ key, sku, cabinetId: skuCabId }) =>
                        request(
                            `/api/v1/tasks/daily-metrics?account_id=${skuCabId}&date_from=${from}&date_to=${to}&skus=${encodeURIComponent(sku)}`
                        )
                            .then(data => ({ key, data: data || [] }))
                            .catch(() => ({ key, data: [] }))
                    )
                );
                const map = {};
                skuFetches.forEach(({ key, data }) => { map[key] = data; });
                setSkuData(map);
            }
            setHasLoaded(true);
        } catch (e) {
            showStatus('error', 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    // Показатели «Логов»: общие + специфичные, если выбранные кабинеты одного маркетплейса
    const shownCabinets = allCabinets
        ? workspaceCabinets
        : workspaceCabinets.filter(c => String(c.id) === String(cabinetId));
    const shownTypes = new Set(shownCabinets.map(c => c.marketplace_type));
    const specificCols = shownTypes.size !== 1 ? [] : (shownTypes.has('wildberries') ? WB_COLS : OZON_COLS)
        .filter(c => !SKIP_KEYS.has(c.key));
    const metrics = [...COMMON_METRICS, ...specificCols];
    const currentMetric = metrics.find(m => m.key === metricType) || metrics[0];

    const buildSeries = (rawData) => rawData.map(d => ({
        date: d.date,
        value: Number(d[currentMetric.key]) || 0,
    }));

    const buildMilestones = (filterSku) =>
        milestones
            .filter(m => filterSku === null ? !m.product_sku : m.product_sku === filterSku)
            .filter(m => m.closed_at)
            // у задачи может быть несколько привязок — на графике она одна веха
            .filter((m, i, arr) => arr.findIndex(x => x.task_id === m.task_id) === i)
            .map(m => ({
                date: m.closed_at?.split('T')[0] || m.closed_at,
                label: m.task_title,
                metric: m.target_metric,
            }));

    const chartTitle = allCabinets
        ? `${workspace?.name || 'Проект'} — Все кабинеты`
        : workspaceCabinets.find(c => String(c.id) === String(cabinetId))?.account_name || 'Кабинет';

    const canAddSkuChart = !allCabinets && cabinetId;

    return (
        <div className="dynamics-tab">
            {/* Controls */}
            <div className="dynamics-tab__controls">

                {/* Workspace selector */}
                <select
                    value={selectedWorkspaceId}
                    onChange={e => { setSelectedWorkspaceId(e.target.value); if (e.target.value) updateUrlParams({ project: e.target.value }, { push: true }); }}
                    className="dynamics-tab__select"
                >
                    <option value="">Выберите проект</option>
                    {visibleWorkspaces.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                </select>

                {/* Cabinet selection — filtered to workspace (Ozon + WB) */}
                {selectedWorkspaceId && workspaceCabinets.length > 0 && (
                    <div className="dynamics-tab__cabinets">
                        <label className="dynamics-tab__cabinet-opt">
                            <input
                                type="radio"
                                checked={allCabinets}
                                onChange={() => { setAllCabinets(true); setCabinetId(''); }}
                            />
                            Все кабинеты
                        </label>
                        {workspaceCabinets.map(cab => (
                            <label key={cab.id} className="dynamics-tab__cabinet-opt">
                                <input
                                    type="radio"
                                    checked={!allCabinets && cabinetId === cab.id}
                                    onChange={() => { setAllCabinets(false); setCabinetId(cab.id); }}
                                />
                                {cab.account_name}
                                {cab.marketplace_type === 'wildberries' ? ' (WB)' : ''}
                            </label>
                        ))}
                    </div>
                )}

                {selectedWorkspaceId && workspaceCabinets.length === 0 && (
                    <p className="dynamics-tab__no-cabinets">
                        В проекте нет подключённых кабинетов.
                    </p>
                )}

                {/* Period */}
                <div className="dynamics-tab__period">
                    <label className="dynamics-tab__period-opt">
                        <input
                            type="radio"
                            checked={periodPreset === '30'}
                            onChange={() => setPeriodPreset('30')}
                        />
                        30 дней
                    </label>
                    <label className="dynamics-tab__period-opt">
                        <input
                            type="radio"
                            checked={periodPreset === 'custom'}
                            onChange={() => setPeriodPreset('custom')}
                        />
                        Произвольный период
                    </label>
                    {periodPreset === 'custom' && (
                        <div className="dynamics-tab__date-range">
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                className="dynamics-tab__date-input"
                            />
                            <span className="dynamics-tab__date-sep">—</span>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                className="dynamics-tab__date-input"
                            />
                        </div>
                    )}
                </div>

                {/* Metric toggle */}
                <div className="dynamics-tab__metric-toggle">
                    {metrics.map(m => (
                        <button
                            key={m.key}
                            type="button"
                            className={`dynamics-tab__metric-btn${currentMetric.key === m.key ? ' active' : ''}`}
                            onClick={() => setMetricType(m.key)}
                        >
                            {m.label}
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className="dynamics-tab__load-btn"
                    onClick={loadData}
                    disabled={loading || !selectedWorkspaceId}
                >
                    {loading ? 'Загрузка...' : 'Загрузить'}
                </button>
            </div>

            {/* Assortment chart */}
            {(hasLoaded || loading) && (
                <div className="dynamics-tab__chart-block">
                    <div className="dynamics-tab__chart-label">
                        <span className="dynamics-tab__chart-title">{chartTitle}</span>
                        <span className="dynamics-tab__chart-sub">
                            {currentMetric.label}
                            {' '}· вехи: задачи с привязкой «Весь ассортимент»
                        </span>
                    </div>
                    {loading ? (
                        <div className="dynamics-tab__loading">Загрузка данных...</div>
                    ) : assortmentData.length === 0 ? (
                        <div className="dynamics-tab__empty">
                            Нет данных за выбранный период.
                            {dataRange && (
                                <span>
                                    {' '}Доступные данные: <b>{dataRange.min}</b> — <b>{dataRange.max}</b>.{' '}
                                    <button
                                        type="button"
                                        className="dynamics-tab__hint-link"
                                        onClick={() => {
                                            setPeriodPreset('custom');
                                            setDateFrom(dataRange.min);
                                            setDateTo(dataRange.max);
                                            loadData(dataRange.min, dataRange.max);
                                        }}
                                    >
                                        Загрузить весь период
                                    </button>
                                </span>
                            )}
                        </div>
                    ) : (
                        <MilestoneLineChart
                            data={buildSeries(assortmentData)}
                            milestones={buildMilestones(null)}
                            color={METRIC_COLORS[currentMetric.color] || '#3b82f6'}
                            label={currentMetric.label}
                        />
                    )}
                </div>
            )}

            {/* Per-SKU charts */}
            {extraSkus.map(({ key, sku, name }) => {
                const raw = skuData[key] || [];
                return (
                    <div key={key} className="dynamics-tab__chart-block">
                        <div className="dynamics-tab__chart-label">
                            <span className="dynamics-tab__chart-title">
                                {sku}{name ? ` — ${name}` : ''}
                            </span>
                            <button
                                type="button"
                                className="dynamics-tab__chart-remove"
                                onClick={() => removeSkuChart(key)}
                            >
                                ×
                            </button>
                        </div>
                        {loading ? (
                            <div className="dynamics-tab__loading">Загрузка...</div>
                        ) : (
                            <MilestoneLineChart
                                data={buildSeries(raw)}
                                milestones={buildMilestones(sku)}
                                color="#9b59b6"
                                label={sku}
                            />
                        )}
                    </div>
                );
            })}

            {/* SKU chart search — only for single specific cabinet */}
            {canAddSkuChart && (
                <div className="dynamics-tab__add-sku">
                    <div className="dynamics-tab__sku-search-wrap" ref={skuSearchRef}>
                        <input
                            type="text"
                            className="dynamics-tab__sku-input"
                            value={skuSearch}
                            onChange={e => setSkuSearch(e.target.value)}
                            placeholder="Добавить артикул для отдельного графика..."
                        />
                        {searchBusy && (
                            <span className="dynamics-tab__sku-busy">Поиск...</span>
                        )}
                        {skuResults.length > 0 && (
                            <ul className="dynamics-tab__sku-results">
                                {skuResults.map(p => (
                                    <li
                                        key={p.sku}
                                        className="dynamics-tab__sku-result"
                                        onMouseDown={() => addSkuChart(p)}
                                    >
                                        <b>{p.sku}</b>{p.name ? ` — ${p.name}` : ''}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {/* Hint */}
            <div className="dynamics-tab__hint">
                ● Жёлтые маркеры — вехи: задачи, закрытые и привязанные к кабинетам.
                Маркер смещён на +2 дня от даты закрытия (чтобы видеть эффект).
                Выручка WB = розничная сумма продаж (до вычета комиссий).
            </div>
        </div>
    );
}
